import Phaser from "phaser";
import type { ScrollableList } from "@yjsgames/phaser-ui";

/**
 * ScrollableList 컨테이너 자식(그리드 셀의 bg + label)을 드래그하는 reparent 패턴을 캡슐화한다 —
 * ModulePaletteView line 122~174의 검증된 시퀀스를 그대로 옮긴 것(merge/fusion 탭 셀에서 재사용).
 *
 * 왜 reparent가 필요한가: Container 자식의 setDepth는 컨테이너 내부 순서만 바꿔 scene draw order를
 * 못 올린다(scrollList.depth로 렌더). dragstart에서 scene 직접 자식으로 올려야 depth 60이 전역 적용된다.
 * reparent 후엔 Phaser drag 좌표(부모 로컬 기준)가 어긋나므로 pointer + 잡은 offset으로 직접 배치한다.
 * 메모리 `phaser-container-drag-gotcha.md` 함정 1~3 회피.
 *
 * 16px(scene.input.dragDistanceThreshold) 미만 이동은 drag 이벤트가 발생하지 않아 `dragged=false`로 남고
 * pointerup에서 onClick이 발동한다 → 드래그와 클릭(정보팝업/해제)이 공존한다.
 *
 * ⚠ 슬롯(scene 직접 자식)에는 사용 금지 — 슬롯은 reparent 불필요(setDepth만으로 최상위).
 */
/**
 * `attachScrollCellDrag`에 전달하는 셀별 설정 — bg·label 게임오브젝트, 홈 좌표, 클릭/드롭 콜백을 묶는다.
 * 드래그/클릭 분기(16px 임계), reparent 복귀, refresh 보류 seam이 모두 이 config를 통해 제어된다.
 */
export interface ScrollCellDragConfig {
  readonly bg: Phaser.GameObjects.Rectangle;
  readonly label: Phaser.GameObjects.Text;
  /**
   * 현재 컨테이너 로컬 home 좌표(lx/ly)를 반환 — dragend 스냅백 위치(`y - scrollList.scrollY`)와
   * reparent 복귀에 사용한다. **함수로 받는 이유**: 셀 풀링 시 같은 셀 객체가 update마다 다른 row/위치로
   * 재사용되므로, 고정 closure가 아니라 호출 시점에 셀의 현재 home을 동적 조회해야 stale 좌표를 막는다.
   */
  readonly getHome: () => { x: number; y: number };
  /** 16px 미만 = 클릭(드래그 아님). 기존 pointerup 분기를 여기로 옮긴다. */
  readonly onClick: () => void;
  /** dragend — pointer는 scene world 좌표. true 반환 시 성공 mutation으로 보고 stale pending flush를 건너뛸 수 있다. */
  readonly onDrop: (pointerX: number, pointerY: number) => boolean | void;
  /**
   * 드래그 시작/종료 알림 — 뷰가 드래그 중 외부 refresh(`update`)의 rebuildCells를 보류하게 한다.
   * ⚠ 합성/융합 탭은 게임 진행 중 주기적으로 refresh되는데, rebuildCells→clearContent가 contentItems에
   * 남은(scene reparent됐어도) 드래그 셀까지 destroy → Phaser 드래그가 중간에 끊긴다. 이를 막는 seam.
   */
  readonly onDragStart?: () => void;
  readonly onDragEnd?: (dropHandled: boolean) => void;
}

/**
 * `ScrollCellDragConfig`를 받아 bg 게임오브젝트에 drag/click 이벤트를 등록한다.
 *
 * 등록 흐름:
 * 1. `dragstart` — scrollList에서 bg·label을 제거하고 scene 직접 자식으로 reparent(depth 60/61),
 *    mask를 해제해 scrollList 클립 영역 밖에서도 드래그 중인 셀이 보이도록 한다.
 * 2. `drag` — pointer + 잡은 offset으로 bg·label을 직접 이동(Phaser drag 이벤트는 부모 기준 좌표라
 *    reparent 후 좌표계가 바뀌므로 직접 배치가 필수).
 * 3. `dragend` — scene 직접 자식 → scrollList(Container) 자식으로 복귀, scrollY 보정 후 mask 재등록.
 *    셀은 항상 home 위치로 스냅백 — 등록 성공 시 호출자의 refresh가 슬롯을 채운다.
 *
 * ⚠ 슬롯(scene 직접 자식)에는 호출 금지 — 슬롯은 이미 최상위라 reparent 없이 setDepth만으로 충분하다.
 * ⚠ `onDragStart/onDragEnd` 콜백 없이 외부 refresh가 rebuildCells를 호출하면 드래그 중인 셀이
 *    destroy될 수 있다 — 주기적으로 refresh하는 탭(합성·융합)에서는 반드시 콜백을 주입해 보류 처리할 것.
 *
 * @param scene - 셀을 reparent할 대상 씬
 * @param scrollList - 셀 bg·label이 원래 소속된 ScrollableList
 * @param cfg - 셀별 게임오브젝트, 홈 좌표, 클릭/드롭 콜백 설정
 */
export function attachScrollCellDrag(
  scene: Phaser.Scene,
  scrollList: ScrollableList,
  cfg: ScrollCellDragConfig,
): void {
  const { bg, label } = cfg;
  bg.setInteractive({ draggable: true, useHandCursor: true });

  let dragged = false;
  let dragOffsetX = 0;
  let dragOffsetY = 0;

  bg.on("pointerdown", () => {
    dragged = false;
  });
  bg.on("pointerup", () => {
    scrollList.suppressDragScroll(false);
    if (!dragged) cfg.onClick();
  });

  bg.on("dragstart", (pointer: Phaser.Input.Pointer) => {
    dragged = true;
    // scene-level 드래그 스크롤(8px 임계)을 억제 — 이 셀의 Phaser 드래그(16px)와 동시 발동 방지.
    scrollList.suppressDragScroll(true);
    const worldX = scrollList.x + bg.x;
    const worldY = scrollList.y + bg.y;
    dragOffsetX = worldX - pointer.x;
    dragOffsetY = worldY - pointer.y;
    scrollList.freezeItem(bg);
    scrollList.freezeItem(label);
    scrollList.remove(bg);
    scrollList.remove(label);
    scene.add.existing(bg);
    scene.add.existing(label);
    bg.clearMask();
    label.clearMask();
    bg.setPosition(worldX, worldY).setDepth(60);
    label.setPosition(worldX, worldY).setDepth(61);
    cfg.onDragStart?.();
  });
  bg.on("drag", (pointer: Phaser.Input.Pointer) => {
    bg.setPosition(pointer.x + dragOffsetX, pointer.y + dragOffsetY);
    label.setPosition(pointer.x + dragOffsetX, pointer.y + dragOffsetY);
  });
  bg.on("dragend", (pointer: Phaser.Input.Pointer) => {
    scrollList.suppressDragScroll(false);
    // dragstart의 reparent를 대칭 복귀: scene 직접 자식 → scrollList(Container) 자식.
    scrollList.unfreezeItem(bg);
    scrollList.unfreezeItem(label);
    scrollList.add(bg);
    scrollList.add(label);
    // 화면상 보이는 위치(home.y - 현재 scrollY)로 보정 + dragstart에서 clearMask한 콘텐츠 클립 복원.
    // home은 호출 시점 동적 조회 — 풀링으로 셀의 home이 바뀌었어도 최신 좌표로 스냅백.
    const home = cfg.getHome();
    bg.setPosition(home.x, home.y - scrollList.scrollY).setDepth(5).setMask(scrollList.contentMask);
    label.setPosition(home.x, home.y - scrollList.scrollY).setDepth(5).setMask(scrollList.contentMask);
    // 셀은 항상 home으로 스냅백된 상태 — 등록 성공 시 호출자의 refresh가 슬롯을 채워 피드백을 준다.
    const dropHandled = cfg.onDrop(pointer.x, pointer.y) === true;
    // 드래그 종료 알림 — 보류된 refresh를 호출자가 flush(드래그 끝났으니 rebuild 안전).
    cfg.onDragEnd?.(dropHandled);
  });
}
